import { formatDateAndTime } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";

// INFO: the action types api-next sends on GET external-action/:id/:key/state.
export enum EExternalActionType {
  ApplicationNoInterest = "ApplicationNoInterest",
  AskRemainingData = "AskRemainingData",
  ContractEnd = "ContractEnd",
  ContractRenew = "ContractRenew",
  IndexationOwnerApprove = "IndexationOwnerApprove",
  IndexationOwnerDecline = "IndexationOwnerDecline",
}

export enum EExternalActionState {
  Open = "OPEN",
  Done = "DONE",
  Unavailable = "UNAVAILABLE",
}

export interface IExternalActionState {
  actionType: EExternalActionType;
  state: EExternalActionState;
  propertyAddress: string | null;
  visitDate: string | null;
}

export interface IExternalActionCopy {
  title: string;
  // Shown under the title when the body does not already name it.
  address: string | null;
  body: string;
  button: string;
  done: string;
}

// INFO: every word on the page comes from here, picked by the action type the API reports.
// Nothing is read from the url: a page that printed url text would let anyone send a link
// to our domain showing a message of their choosing (DEV-3330).
export const getExternalActionCopy = (
  externalActionState: IExternalActionState,
): IExternalActionCopy | undefined => {
  const { actionType, propertyAddress, visitDate } = externalActionState;

  switch (actionType) {
    case EExternalActionType.ApplicationNoInterest: {
      // The same sentences the candidate reads in their applications screen.
      let body = getLocalizedText("rentmatch.no_interest.page.body_no_address");
      if (propertyAddress && visitDate) {
        body = getLocalizedText("rentmatch.no_interest.page.body_with_visit", {
          address: propertyAddress,
          date: formatDateAndTime(visitDate),
        });
      } else if (propertyAddress) {
        body = getLocalizedText("rentmatch.no_interest.page.body", {
          address: propertyAddress,
        });
      }

      // INFO: with a visit booked, the thank-you also says it is cancelled and that the
      // provider knows, so the candidate does not call the office to make sure. True when it
      // shows: api-next cancels the visit, and mails the office, before it answers the press
      // (DEV-3547). A link reopened later reports no visit, and gets the plain thank-you.
      const done = visitDate
        ? getLocalizedText("rentmatch.no_interest.page.done_with_visit", {
            date: formatDateAndTime(visitDate),
          })
        : getLocalizedText("rentmatch.no_interest.page.done");

      return {
        title: getLocalizedText("rentmatch.no_interest.page.title"),
        address: null,
        body,
        button: getLocalizedText("rentmatch.application.no_interest.button"),
        done,
      };
    }

    case EExternalActionType.ContractEnd:
      return {
        title: getLocalizedText("external_action.contract_end.title"),
        address: propertyAddress,
        body: getLocalizedText("external_action.contract_end.body"),
        button: getLocalizedText("external_action.contract_end.button"),
        done: getLocalizedText("external_action.done"),
      };

    case EExternalActionType.ContractRenew:
      return {
        title: getLocalizedText("external_action.contract_renew.title"),
        address: propertyAddress,
        body: getLocalizedText("external_action.contract_renew.body"),
        button: getLocalizedText("external_action.contract_renew.button"),
        done: getLocalizedText("external_action.done"),
      };

    case EExternalActionType.IndexationOwnerApprove:
      return {
        title: getLocalizedText("external_action.indexation_approve.title"),
        address: propertyAddress,
        body: getLocalizedText("external_action.indexation_approve.body"),
        button: getLocalizedText("external_action.indexation_approve.button"),
        done: getLocalizedText("external_action.done"),
      };

    case EExternalActionType.IndexationOwnerDecline:
      return {
        title: getLocalizedText("external_action.indexation_decline.title"),
        address: propertyAddress,
        body: getLocalizedText("external_action.indexation_decline.body"),
        button: getLocalizedText("external_action.indexation_decline.button"),
        done: getLocalizedText("external_action.done"),
      };

    // AskRemainingData is a form in the tenant dashboard, never a page with one button.
    default:
      return undefined;
  }
};

// INFO: the old action types finish on a page of their own on rentio.be, which the API
// returns after the press. The candidacy link does not: its thank-you is on this page.
export const followsRedirectAfterPress = (actionType: EExternalActionType) =>
  actionType !== EExternalActionType.ApplicationNoInterest;

// INFO: the thank-you pages the older action types finish on all live on rentio.be. A
// redirect anywhere else is refused, so a wrong or tampered API answer can never send the
// reader off our domain from a page on it.
export const isAllowedRedirect = (redirectUrl: string) => {
  try {
    const { protocol, hostname } = new URL(redirectUrl);

    return (
      (protocol === "https:" || protocol === "http:") &&
      (hostname === "rentio.be" || hostname.endsWith(".rentio.be"))
    );
  } catch {
    return false;
  }
};
